/**
 * SplitSection Component
 * Displays and edits revenue split configuration from CreditSplitConfig_GT
 * Shows fiado_rate_points, partner_rate_points, and iva_rate with visual bar
 */

import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Pencil } from 'lucide-react';
import { ConfirmSaveModal } from '../ConfirmSaveModal';
import type { SplitConfigItem } from '@/types/policy.types';

interface SplitSectionProps {
  data: SplitConfigItem[] | null;
  canEdit: boolean;
  onSave: (data: SplitConfigItem[]) => void;
  isSaving: boolean;
}

export function SplitSection({ data, canEdit, onSave, isSaving }: SplitSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<SplitConfigItem | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // The API returns an array but there's only one split config item
  const splitItem = data?.[0] ?? null;

  if (!splitItem) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin configuracion de split disponible.
      </p>
    );
  }

  function startEditing() {
    setDraft(structuredClone(splitItem));
    setIsEditing(true);
  }

  function cancelEditing() {
    setDraft(null);
    setIsEditing(false);
  }

  function handleConfirm() {
    if (draft) {
      onSave([draft]);
    }
    setShowConfirm(false);
    setIsEditing(false);
    setDraft(null);
  }

  function updateDraft<K extends keyof SplitConfigItem>(field: K, value: SplitConfigItem[K]) {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  const current = isEditing && draft ? draft : splitItem;
  const totalPoints = current.fiado_rate_points + current.partner_rate_points;
  const fiadoPct = totalPoints > 0 ? (current.fiado_rate_points / totalPoints) * 100 : 0;
  const partnerPct = totalPoints > 0 ? (current.partner_rate_points / totalPoints) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex justify-end">
        {canEdit && !isEditing && (
          <Button variant="outline" size="sm" leftIcon={<Pencil size={16} />} onClick={startEditing}>
            Editar
          </Button>
        )}
        {isEditing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={cancelEditing}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowConfirm(true)}>
              Guardar
            </Button>
          </div>
        )}
      </div>

      {/* Visual split bar */}
      <Card title="Distribucion de Intereses">
        <div className="space-y-4">
          {/* Stacked bar */}
          <div className="h-10 w-full overflow-hidden rounded-lg flex">
            <div
              className="flex items-center justify-center bg-primary text-xs font-medium text-primary-foreground transition-all"
              style={{ width: `${fiadoPct}%` }}
            >
              {fiadoPct > 5 && `${current.fiado_rate_points} pts`}
            </div>
            <div
              className="flex items-center justify-center bg-blue-500 text-xs font-medium text-white transition-all"
              style={{ width: `${partnerPct}%` }}
            >
              {partnerPct > 5 && `${current.partner_rate_points} pts`}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-primary" />
              <span>Fiado: <strong>{current.fiado_rate_points} puntos</strong></span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm bg-blue-500" />
              <span>UNE-Credito: <strong>{current.partner_rate_points} puntos</strong></span>
            </div>
          </div>

          {/* Edit inputs or view details */}
          {isEditing ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Input
                label="Fiado (puntos)"
                type="number"
                step="1"
                value={current.fiado_rate_points}
                onChange={(e) => updateDraft('fiado_rate_points', Number(e.target.value))}
              />
              <Input
                label="UNE-Credito (puntos)"
                type="number"
                step="1"
                value={current.partner_rate_points}
                onChange={(e) => updateDraft('partner_rate_points', Number(e.target.value))}
              />
              <Input
                label="Tasa IVA"
                type="number"
                step="0.01"
                value={current.iva_rate}
                onChange={(e) => updateDraft('iva_rate', Number(e.target.value))}
                hint="Decimal (ej: 0.16 = 16%)"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground">Tasa Total</p>
                <p className="text-2xl font-bold">{totalPoints} puntos</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Tasa IVA</p>
                <p className="text-2xl font-bold">{(current.iva_rate * 100).toFixed(0)}%</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">Fecha Efectiva</p>
                <p className="text-sm font-medium">{current.effective_date}</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      <ConfirmSaveModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isSaving={isSaving}
      />
    </div>
  );
}
